import {
  useAuthStore,
  useCheckoutStore,
  useErrorTrackerHandler,
  useTranslation,
} from 'mycheck-core';
import {
  AstralWalletTransactionStatus,
  AstralWalletTransactionStatusResponseType,
  HttpCheckoutApiResponse,
} from 'plugins/CheckoutPlugin/types/CheckoutTypes';
import React, { useEffect, useState } from 'react';
import { usePolling } from 'core/hooks/usePolling';
import { observer } from 'mobx-react';
import { useAstralWalletParentMessageListener } from 'plugins/CheckoutPlugin/components/PaymentsMethod/AstralWallet/hooks/useAstralWalletParentMessageListener';
import { AstralWalletStatus } from './AstralWalletStatus/AstralWalletStatus';
import { astralWalletPostMessageToParent } from 'plugins/CheckoutPlugin/components/PaymentsMethod/AstralWallet/utils/astralWalletPostMessageToParent';
import { AstralWalletIFrameMessage } from 'plugins/CheckoutPlugin/components/PaymentsMethod/AstralWallet/constants';

const AstralWalletTransactionStatusPage = observer(() => {
  const { t } = useTranslation();
  const [transactionStatusData, setTransactionStatusData] =
    useState<AstralWalletTransactionStatusResponseType>(null);
  const CheckoutStore = useCheckoutStore();
  const AuthStore = useAuthStore();
  const errorHandler = useErrorTrackerHandler();

  const url = new URL(document.location.href);
  const id = url.searchParams.get('id');

  const fetchAstralWalletTransactionStatus = async () => {
    CheckoutStore.setAstralWalletLoaderLabel(
      t('astralWallet.submittingOrderLoaderLabel'),
    );
    return await CheckoutStore.astralWalletTransactionStatus({
      accessToken: AuthStore.accessToken,
      uuid: id,
    });
  };

  const handleShouldStopAstrallWalletStatusInterval = (
    result: HttpCheckoutApiResponse['iAstralWalletTransactionStatus'],
  ) => {
    const pendingTransactionStatuses = [
      AstralWalletTransactionStatus.PENDING,
      AstralWalletTransactionStatus.IN_PROGRESS,
    ];
    const { order, payment } = result;

    const isProcessing =
      pendingTransactionStatuses.includes(order.status) ||
      pendingTransactionStatuses.includes(payment.status);

    if (!isProcessing) {
      setTransactionStatusData(result);
      return true;
    }
  };

  const handleIntervalErrorCallback = (err: Error) => {
    errorHandler.onError(err, () => {
      astralWalletPostMessageToParent({
        type: AstralWalletIFrameMessage.CHANGE_PAYMENT_METHOD,
      });
    });
  };

  const {
    start: startFetchAstralWalletTransactionStatusInterval,
    stop: stopFetchAstralWalletTransactionStatusInterval,
    isPolling,
  } = usePolling<HttpCheckoutApiResponse['iAstralWalletTransactionStatus']>({
    pollFn: fetchAstralWalletTransactionStatus,
    options: {
      onErrorCallback: handleIntervalErrorCallback,
      shouldStopFn: handleShouldStopAstrallWalletStatusInterval,
    },
  });

  useAstralWalletParentMessageListener();

  useEffect(() => {
    (async () => {
      astralWalletPostMessageToParent({
        type: AstralWalletIFrameMessage.LOCK_ASTRAL_WALLET,
      });
      await AuthStore.getGuestRefreshToken();
      await AuthStore.getGuestLogin();
      startFetchAstralWalletTransactionStatusInterval();
    })();

    return () => stopFetchAstralWalletTransactionStatusInterval();
  }, []);

  return (
    <AstralWalletStatus
      retryAstralWalletStatusInterval={
        startFetchAstralWalletTransactionStatusInterval
      }
      transactionStatusData={transactionStatusData}
      isLoading={
        isPolling ||
        CheckoutStore.isAstralWalletLoading ||
        !transactionStatusData
      }
    >
      <AstralWalletStatus.Loader />
      <AstralWalletStatus.CheckoutFailed />
      <AstralWalletStatus.PaymentFailed />
    </AstralWalletStatus>
  );
});

export default AstralWalletTransactionStatusPage;
